// Company-wide Realtor Directory.
//
// Stored on the company profile as `companyProfile.realtor_directory` (an array).
// The company profile is persisted as a single JSON blob ("content"), and the read
// path spreads the parsed JSON back, so a new field like this persists with no
// backend/schema change (same mechanism that lets realtor_contacts save).
//
// A realtor is a PERSON, not a role — the same agent can be the Listing Agent on one
// loan and the Buyer's Agent on the next (or both on the same loan). Role lives on the
// loan file, never on the directory record.

export const REALTOR_FIELDS = [
  "name",
  "firm",
  "office_phone",
  "cell_phone",
  "email",
  "license"
];

function norm(v) {
  // Trim, and strip control chars + the U+FFFD replacement character that Excel
  // exports leave behind (seen as garbled bytes on license #s and dashes).
  return (v == null ? "" : String(v))
    .split("")
    .filter(function(ch){ var c = ch.charCodeAt(0); return c > 31 && c !== 127 && c !== 0xFFFD; })
    .join("")
    .trim();
}

// --- CSV bulk upload / template -------------------------------------------

// Friendly column headers the loan officer sees, in order, mapped to our fields.
export const REALTOR_CSV_COLUMNS = [
  { header: "Name", field: "name" },
  { header: "Company", field: "firm" },
  { header: "Office Ph", field: "office_phone" },
  { header: "Cell", field: "cell_phone" },
  { header: "Email", field: "email" },
  { header: "License #", field: "license" }
];

/** Header + one example row, for the downloadable template. */
export function realtorCsvTemplate() {
  const header = REALTOR_CSV_COLUMNS.map(c => c.header).join(",");
  const example =
    "Jane Smith,Keller Williams Realty,702-555-1000,702-555-2000,jane@kw.com,S.0123456";
  return `${header}\n${example}\n`;
}

/** Escape a single CSV cell (quote when it contains a comma, quote, or newline). */
function realtorCsvCell(value) {
  const s = value == null ? "" : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * The current Realtor Directory as CSV text, in the SAME columns as the upload
 * template — so a company can export a copy of their list and re-import it
 * anywhere. Round-trips cleanly with realtorsFromCsv / the upload.
 */
export function realtorDirectoryToCsv(directory) {
  const header = REALTOR_CSV_COLUMNS.map(c => c.header).join(",");
  const rows = (directory || []).map(realtor =>
    REALTOR_CSV_COLUMNS.map(c => realtorCsvCell(realtor[c.field])).join(",")
  );
  return [header, ...rows].join("\n") + "\n";
}

/**
 * Minimal RFC-4180-ish CSV parser: handles quoted fields (so commas inside a
 * company name survive), escaped quotes, and CRLF/LF line endings. Good enough
 * for the realtor list without pulling in a dependency.
 */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const s = String(text || "").replace(/^﻿/, ""); // drop BOM

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  // trailing field/row (file may not end in a newline)
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * Turn CSV text into realtor records, mapping columns by header name (order-
 * independent, case-insensitive). Blank rows are dropped. Returns [] if the
 * header row doesn't contain any recognized column.
 */
export function realtorsFromCsv(text) {
  const rows = parseCsv(text).filter(r => r.some(cell => norm(cell)));
  if (rows.length === 0) return [];

  const headerCells = rows[0].map(h => norm(h).toLowerCase());
  const colIndex = {};
  REALTOR_CSV_COLUMNS.forEach(({ header, field }) => {
    const idx = headerCells.indexOf(header.toLowerCase());
    if (idx !== -1) colIndex[field] = idx;
  });
  if (Object.keys(colIndex).length === 0) return [];

  return rows
    .slice(1)
    .map(cells => {
      const rec = {};
      REALTOR_FIELDS.forEach(f => {
        rec[f] = colIndex[f] != null ? norm(cells[colIndex[f]]) : "";
      });
      return rec;
    })
    .filter(rec => !isBlankRealtor(rec));
}

/**
 * Merge a batch of incoming realtors into the directory, reporting what
 * happened for the upload summary.
 * Returns { directory, added, updated, skipped, details }.
 *   details: [{ name, firm, type: "added"|"updated", changes? }]
 */
export function mergeRealtors(directory, incoming) {
  let list = Array.isArray(directory) ? [...directory] : [];
  let added = 0;
  let updated = 0;
  let skipped = 0;
  const details = [];

  (incoming || []).forEach(raw => {
    const rec = pick(raw);
    if (isBlankRealtor(rec)) {
      skipped++;
      return;
    }
    const key = realtorKey(rec);
    if (!key) {
      skipped++;
      return;
    }

    const email = norm(rec.email).toLowerCase();
    let index = list.findIndex(r => realtorKey(r) === key);
    if (index === -1 && email) {
      index = list.findIndex(r => norm(r.email).toLowerCase() === email);
    }

    if (index === -1) {
      list = [...list, rec];
      added++;
      details.push({ name: rec.name, firm: rec.firm, type: "added" });
      return;
    }

    const existing = list[index];
    const merged = { ...existing };
    const changes = [];
    REALTOR_FIELDS.forEach(f => {
      if (rec[f] && norm(rec[f]) !== norm(existing[f])) {
        merged[f] = rec[f];
        changes.push(f);
      }
    });

    if (changes.length === 0) {
      skipped++; // already present, nothing new
      return;
    }

    list = [...list];
    list[index] = merged;
    updated++;
    details.push({
      name: merged.name,
      firm: merged.firm,
      type: "updated",
      changes
    });
  });

  return { directory: list, added, updated, skipped, details };
}

/**
 * Identity for a realtor. License # almost never changes, so it is the key.
 * Falls back to email, then name+firm, for records saved before a license is entered.
 */
export function realtorKey(r) {
  if (!r) return "";
  const license = norm(r.license).toUpperCase();
  if (license) return `L:${license}`;
  const email = norm(r.email).toLowerCase();
  if (email) return `E:${email}`;
  const name = norm(r.name).toLowerCase();
  const firm = norm(r.firm).toLowerCase();
  return name || firm ? `N:${name}|${firm}` : "";
}

/** True when there is nothing worth saving to the directory. */
export function isBlankRealtor(r) {
  if (!r) return true;
  return !REALTOR_FIELDS.some(f => norm(r[f]));
}

function pick(r) {
  const out = {};
  REALTOR_FIELDS.forEach(f => (out[f] = norm(r[f])));
  return out;
}

/**
 * Add or update one realtor in the directory (auto-add on Save).
 * - Matches on realtorKey (License # first).
 * - If an existing record has no license yet and this one supplies a matching
 *   email, the license is merged onto that record rather than creating a duplicate.
 * - Only non-empty incoming values overwrite, so a partially filled form never
 *   wipes details captured earlier.
 * Returns a NEW array (or the original if nothing changed).
 */
export function mergeRealtor(directory, incoming) {
  const list = Array.isArray(directory) ? directory : [];
  if (isBlankRealtor(incoming)) return list;

  const rec = pick(incoming);
  const key = realtorKey(rec);
  if (!key) return list;

  // Direct key match, or an older record with the same email that predates the license.
  const email = norm(rec.email).toLowerCase();
  let index = list.findIndex(r => realtorKey(r) === key);
  if (index === -1 && email) {
    index = list.findIndex(r => norm(r.email).toLowerCase() === email);
  }

  if (index === -1) {
    return [...list, rec];
  }

  const existing = list[index];
  const merged = { ...existing };
  REALTOR_FIELDS.forEach(f => {
    if (rec[f]) merged[f] = rec[f];
  });
  // No actual change — keep the same array so we don't trigger a pointless save.
  if (REALTOR_FIELDS.every(f => norm(existing[f]) === norm(merged[f]))) return list;

  const next = [...list];
  next[index] = merged;
  return next;
}

/** Replace one record (used by the Full Realtor List editor). Future picks only. */
export function updateRealtorAt(directory, index, updated) {
  const list = Array.isArray(directory) ? directory : [];
  if (index < 0 || index >= list.length) return list;
  const next = [...list];
  next[index] = pick(updated);
  return next;
}

export function removeRealtorAt(directory, index) {
  const list = Array.isArray(directory) ? directory : [];
  if (index < 0 || index >= list.length) return list;
  return list.filter((r, i) => i !== index);
}

/** Search by first name, last name, or company. Empty query returns everything. */
export function searchRealtors(directory, query) {
  const list = Array.isArray(directory) ? directory : [];
  const q = norm(query).toLowerCase();
  if (!q) return list;
  const terms = q.split(/\s+/).filter(Boolean);
  return list.filter(r => {
    const hay = `${norm(r.name)} ${norm(r.firm)}`.toLowerCase();
    return terms.every(t => hay.includes(t));
  });
}

/** Alphabetical by name for the directory/list views. */
export function sortRealtors(directory) {
  const list = Array.isArray(directory) ? directory : [];
  return [...list].sort((a, b) =>
    norm(a.name).toLowerCase().localeCompare(norm(b.name).toLowerCase())
  );
}
